import { mutate } from 'swr';
import { fetchUrlWithCookies } from 'contexts/user';
import {
  CommunityPost,
  UserProfile,
  CommunityRemix,
  ShotRemix,
} from 'types/community';
import {
  getCommunityPostReactionsUrl,
  getCommunityProfileUrl,
  getUpdateCommunityReactionUrl,
} from '../community';
import {
  CreateRemixError,
  CreateRemixResponse,
  OwnerRemix,
  Remix,
} from 'types/remixes';

/**
 * Fetch a Community Post by ID and convert to our community shot type.
 *
 * We generally should prefer to use the `useSWR` over this function.
 */
export const fetchCommunityShot = async (
  communityPostId: number,
  communityRevisionId: number | undefined
) => {
  const { NEXT_PUBLIC_API: domain } = process.env;

  const endpoint = `/api/community-posts/${communityPostId}`;
  const response = await fetch(`${domain}${endpoint}`);
  if (response.status === 404) {
    return null;
  }
  const body = await response.json();

  return CommunityPost.fromAPI(body).toCommunityShotItem(communityRevisionId);
};

/**
 * Fetch the community profile from the api.
 *
 * @param username User of the profile to fetch
 * @returns CommunityUserProfile object
 */
export const fetchCommunityProfile = async (username: string | undefined) => {
  const { NEXT_PUBLIC_API: domain } = process.env;
  const endpoint = getCommunityProfileUrl(username);
  const response = await fetch(`${domain}${endpoint}`);
  if (response.status === 404) {
    return null;
  }

  const data = await response.json();
  return UserProfile.fromAPI(data).toCommunityUserProfile();
};

export const updateCommunityReaction = async (
  communityPostId: number,
  reactionId: number
) => {
  const url = getUpdateCommunityReactionUrl(communityPostId, reactionId);
  const response = await fetchUrlWithCookies(url, { method: 'PUT' });

  const error = await response.text();
  const success = response.status === 200 || response.status === 302;

  mutate(getCommunityPostReactionsUrl(communityPostId));
  return { success, error };
};

export const removeCommunityReaction = async (communityPostId: number) => {
  const url = getCommunityPostReactionsUrl(communityPostId);
  const response = await fetchUrlWithCookies(url, { method: 'DELETE' });

  const error = await response.text();
  const success = response.status === 200 || response.status === 302;

  mutate(getCommunityPostReactionsUrl(communityPostId));
  return { success, error };
};

export const addCommunityComment = async ({
  communityPostId,
  communityRevisionId,
  commentData,
}: {
  communityPostId: number;
  communityRevisionId: number;
  commentData: any;
}) => {
  const url = `/api/community-posts/${communityPostId}/comment`;
  const response = await fetchUrlWithCookies(url, {
    method: 'POST',
    body: JSON.stringify({
      communityRevisionId,
      data: commentData,
    }),
  });
  const error = await response.text();
  const success = response.status === 200 || response.status === 302;

  mutate(`/api/community-posts/${communityPostId}/comments`);
  return { success, error };
};

export const replyToCommunityComment = async ({
  communityPostId,
  communityRevisionId,
  commentData,
  replyToCommentId,
}: {
  communityPostId: number;
  communityRevisionId: number;
  commentData: any;
  replyToCommentId?: number;
}) => {
  const url = `/api/comments/${replyToCommentId}/reply`;
  const response = await fetchUrlWithCookies(url, {
    method: 'POST',
    body: JSON.stringify({
      communityRevisionId,
      data: commentData,
    }),
  });
  const error = await response.text();
  const success = response.status === 200 || response.status === 302;

  mutate(`/api/community-posts/${communityPostId}/comments`);
  return { success, error };
};

export const flagComment = async (
  commentId: number,
  communityPostId: number
) => {
  const response = await fetchUrlWithCookies(
    `/api/comments/${commentId}/flag`,
    {
      method: 'POST',
    }
  );
  const success = response.status === 204;
  mutate(`/api/community-posts/${communityPostId}/comments`);
  return { success };
};

export const deleteComment = async (
  commentId: number,
  communityPostId: number
) => {
  const response = await fetchUrlWithCookies(`/api/comments/${commentId}`, {
    method: 'DELETE',
  });
  const success = response.status === 204;
  mutate(`/api/community-posts/${communityPostId}/comments`);
  return { success };
};

export const createRemix = async (
  teamId: number,
  revisionId: number,
  name: string
): Promise<CreateRemixResponse> => {
  const response = await fetchUrlWithCookies(
    `/api/revisions/${revisionId}/remix`,
    {
      method: 'POST',
      body: JSON.stringify({ name, teamId }),
    }
  );

  if (response.status === 200) {
    const remix: Remix = await response.json();
    return { remix };
  }

  switch (response.status) {
    case 401:
      return { error: CreateRemixError.authentication };
    case 403:
      return { error: CreateRemixError.forbidden };
    case 404:
      return { error: CreateRemixError.notFound };
    default:
      return { error: CreateRemixError.unknown };
  }
};

export const fetchCommunityRemixes = async (
  communityPostId: number
): Promise<ShotRemix[]> => {
  const res = await fetchUrlWithCookies(
    `/api/community-posts/${communityPostId}/remixes`
  );
  if (res.status !== 200) {
    return [];
  }
  const data: { remixes: any[] } = await res.json();
  return data.remixes.map((remix) =>
    CommunityRemix.fromAPI(remix).toShotRemix()
  );
};

export const fetchOwnerRemixes = async (
  communityRevisionId: number
): Promise<OwnerRemix[]> => {
  const res = await fetchUrlWithCookies(
    `/api/community-revisions/${communityRevisionId}/user-remixes`
  );

  if (res.status !== 200) {
    return [];
  }

  const data: { remixes: any[] } = await res.json();

  return data.remixes?.map((remix) => remix as OwnerRemix) ?? [];
};
